import { Suspense, lazy, useEffect, useState } from 'react'
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import './App.css'
import akaraLogo from './assets/akara-logo.png'
import akaraLogoWebp from './assets/akara-logo.webp'
import ProtectedRoute from './components/ProtectedRoute'
import AdminRoute from './components/AdminRoute'
import useAuth from './components/useAuth'
import MobileBottomNav from './components/MobileBottomNav'
import CookieBanner from './components/CookieBanner'
import OptimizedImage from './components/OptimizedImage'
import LanguageSwitcher from './components/LanguageSwitcher'
import { useLocale } from './i18n/useLocale'
import { useUi } from './i18n/ui'
import { LANGUAGE_OPTIONS as LANGS } from './i18n/deposit'
import { buildStructuredData } from './seo/structuredData.js'
import { resolveHostRedirect, appHref, APEX_ORIGIN, DOCS_ORIGIN } from './lib/hostRouting.js'

/**
 * Wrap React.lazy so a failed chunk fetch (typically a stale hash after a new
 * deploy) triggers a one-time hard reload to pull fresh assets, instead of
 * silently white-screening. If it still fails after the reload, the error
 * propagates to the error boundary.
 */
const CHUNK_RELOAD_KEY = 'akara-chunk-reloaded'
function lazyWithReload(importFn) {
  return lazy(() =>
    importFn()
      .then((mod) => {
        // Chunk loaded — clear the one-shot guard so a FUTURE stale deploy can
        // reload again. Clearing on SUCCESS (not on every App mount) is what
        // keeps a persistently unfetchable chunk from reloading forever: the
        // old code cleared this in an App effect that ran while the chunk was
        // still in flight, so a 404 chunk reloaded → remounted → cleared →
        // 404 → reloaded… an infinite refresh loop with a blank page.
        try {
          sessionStorage.removeItem(CHUNK_RELOAD_KEY)
        } catch {
          /* ignore */
        }
        return mod
      })
      .catch((error) => {
        const alreadyReloaded =
          typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem(CHUNK_RELOAD_KEY)
        if (!alreadyReloaded && typeof window !== 'undefined') {
          try {
            sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
          } catch {
            /* ignore */
          }
          window.location.reload()
          return new Promise(() => {})
        }
        throw error
      }),
  )
}

const LandingPage = lazyWithReload(() => import('./pages/LandingPage'))
const PropertyManagers = lazyWithReload(() => import('./pages/PropertyManagers'))
const Healthcare = lazyWithReload(() => import('./pages/Healthcare'))
const VehicleRental = lazyWithReload(() => import('./pages/VehicleRental'))
const EquipmentRental = lazyWithReload(() => import('./pages/EquipmentRental'))
const DepositFree = lazyWithReload(() => import('./pages/DepositFree'))
const HowCardHoldsWork = lazyWithReload(() => import('./pages/HowCardHoldsWork'))
const DocsLayout = lazyWithReload(() => import('./pages/docs/DocsLayout'))
const DocsHome = lazyWithReload(() => import('./pages/docs/DocsHome'))
const DocsQuickstart = lazyWithReload(() => import('./pages/docs/DocsQuickstart'))
const DocsApiReference = lazyWithReload(() => import('./pages/docs/DocsApiReference'))
const DocsIntegrations = lazyWithReload(() => import('./pages/docs/DocsIntegrations'))
const DocsIntegrationDetail = lazyWithReload(() => import('./pages/docs/DocsIntegrationDetail'))
const Support = lazyWithReload(() => import('./pages/Support'))
const ContactUs = lazyWithReload(() => import('./pages/ContactUs'))
const PrivacyPolicy = lazyWithReload(() => import('./pages/PrivacyPolicy'))
const Terms = lazyWithReload(() => import('./pages/Terms'))
const FaqPage = lazyWithReload(() => import('./pages/FaqPage'))
const Pricing = lazyWithReload(() => import('./pages/Pricing'))
const Blog = lazyWithReload(() => import('./pages/Blog'))
const BlogPost = lazyWithReload(() => import('./pages/BlogPost'))
const Login = lazyWithReload(() => import('./pages/Login'))
const DashboardShell = lazyWithReload(() => import('./pages/DashboardShell'))
const DashboardHome = lazyWithReload(() => import('./pages/DashboardHome'))
const DashboardRequests = lazyWithReload(() => import('./pages/DashboardRequests'))
const DashboardClaims = lazyWithReload(() => import('./pages/DashboardClaims'))
const DashboardReports = lazyWithReload(() => import('./pages/DashboardReports'))
const DashboardIntegrations = lazyWithReload(() => import('./pages/DashboardIntegrations'))
const DashboardSettingsAccount = lazyWithReload(() => import('./pages/DashboardSettingsAccount'))
const DashboardSettingsPayouts = lazyWithReload(() => import('./pages/DashboardSettingsPayouts'))
const DashboardSettingsSecurity = lazyWithReload(() => import('./pages/DashboardSettingsSecurity'))
const DashboardBilling = lazyWithReload(() => import('./pages/DashboardBilling'))
const DashboardNotifications = lazyWithReload(() => import('./pages/DashboardNotifications'))
const DashboardWebhooks = lazyWithReload(() => import('./pages/DashboardWebhooks'))
const GetStarted = lazyWithReload(() => import('./pages/GetStarted'))
const Waitlist = lazyWithReload(() => import('./pages/Waitlist'))
const Onboarding = lazyWithReload(() => import('./pages/Onboarding'))
const ResetPassword = lazyWithReload(() => import('./pages/ResetPassword'))
const DepositView = lazyWithReload(() => import('./pages/DepositView'))
const AdminShell = lazyWithReload(() => import('./pages/admin/AdminShell'))
const AdminHome = lazyWithReload(() => import('./pages/admin/AdminHome'))
const AdminUsers = lazyWithReload(() => import('./pages/admin/AdminUsers'))
const AdminUserDetail = lazyWithReload(() => import('./pages/admin/AdminUserDetail'))
const AdminDeposits = lazyWithReload(() => import('./pages/admin/AdminDeposits'))
const AdminClaims = lazyWithReload(() => import('./pages/admin/AdminClaims'))
const AdminEscalations = lazyWithReload(() => import('./pages/admin/AdminEscalations'))

/* ── Header dropdown menus → individual pages ── */
const SOLUTION_MENU = [
  {
    title: 'Property Managers',
    desc: 'Automate deposits across your PMS or channel manager.',
    to: '/solutions/property-managers',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 11l9-7 9 7" />
        <path d="M5 10v10h14V10" />
        <path d="M9 20v-6h6v6" />
      </svg>
    ),
  },
  {
    title: 'Healthcare',
    desc: 'Secure out-of-pocket costs and cut overdue balances.',
    to: '/solutions/healthcare',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 5v14" />
        <path d="M5 12h14" />
      </svg>
    ),
  },
  {
    title: 'Vehicle Rental',
    desc: 'Protect your fleet and speed up the counter.',
    to: '/solutions/vehicle-rental',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 12h18" />
        <path d="M5 12l2-5h10l2 5" />
        <circle cx="7" cy="16" r="2" />
        <circle cx="17" cy="16" r="2" />
      </svg>
    ),
  },
  {
    title: 'Equipment Rental',
    desc: 'Digitize deposits so crews collect gear faster.',
    to: '/solutions/equipment-rental',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2v4" />
        <path d="M7 6l2 4-2 4-4-2 2-4z" />
        <path d="M17 6l4 2-2 4-4-2 2-4z" />
        <path d="M10 14l2 8" />
        <path d="M14 14l-2 8" />
      </svg>
    ),
  },
]

// Canonical docs live on the docs.akara.ae subdomain (a Netlify domain alias of
// this same site). Marketing links point there directly; host-based routing (see
// hostRouting.js + the redirect effect in App) bounces any /docs* or app path to
// its correct subdomain, so each section is only served under its own host.

const RESOURCE_MENU = [
  { id: 'documentation', to: `${DOCS_ORIGIN}/docs`, external: true },
  { id: 'webhooks', to: `${DOCS_ORIGIN}/docs/api`, external: true },
  { id: 'support', to: '/support' },
  { id: 'faq', to: '/faq' },
]

function App() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [openMenu, setOpenMenu] = useState(null)
  const location = useLocation()
  const isHome = location.pathname === '/'
  const isDashboard = location.pathname.startsWith('/dashboard')
  const isAdmin = location.pathname.startsWith('/admin')
  const isDocs = location.pathname.startsWith('/docs')
  // docs.akara.ae is a domain alias of this same Netlify site. On that host,
  // the root path serves the docs section (its own chrome), not the landing page.
  const isDocsHost =
    typeof window !== 'undefined' &&
    window.location.hostname.startsWith('docs.')
  // Docs has its own sidebar + theme toggle, so suppress the marketing shell.
  const isAppShell = isDashboard || isAdmin || isDocs
  // Auth pages live on app.akara.ae. They get a stripped-down header — logo
  // only, no nav — so the app subdomain never shows the full marketing banner.
  // Prerendered routes are served with a trailing slash (/get-started/), so
  // match against the slash-less form or the marketing banner leaks back in.
  const normalizedPath =
    location.pathname.length > 1 && location.pathname.endsWith('/')
      ? location.pathname.slice(0, -1)
      : location.pathname
  const isAuthRoute = ['/login', '/get-started', '/reset-password'].includes(
    normalizedPath,
  )
  // The logo always returns to the marketing landing page. From the app or docs
  // subdomain that means an absolute hop back to the apex; on the apex, localhost,
  // and previews it's an in-place SPA link.
  const isSubHost =
    typeof window !== 'undefined' &&
    (window.location.hostname.startsWith('app.') || isDocsHost)
  const landingHref = isSubHost ? `${APEX_ORIGIN}/` : '/'
  const { isAuthed } = useAuth()
  const { locale, setLocale, dir } = useLocale()
  const ui = useUi()

  // Host-based routing across the domain aliases of this one build: app paths →
  // app.akara.ae, docs paths → docs.akara.ae, everything else → apex. Only the
  // real production hosts redirect; localhost and deploy previews render every
  // path in place so the whole app stays testable on one origin. See
  // hostRouting.js.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const target = resolveHostRedirect(
      window.location.hostname,
      location.pathname,
      location.search,
      location.hash,
    )
    if (target && target !== window.location.href) {
      window.location.replace(target)
    }
  }, [location.pathname, location.search, location.hash])

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24)
    }

    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://akara.ae'

    const path = location.pathname
    const scriptId = 'akara-jsonld'
    const schemaItems = buildStructuredData(path, origin)
    if (schemaItems.length === 0) {
      const existing = document.getElementById(scriptId)
      if (existing) existing.remove()
      return
    }

    let script = document.getElementById(scriptId)
    if (!script) {
      script = document.createElement('script')
      script.type = 'application/ld+json'
      script.id = scriptId
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(schemaItems)
  }, [location.pathname])

  const sectionHref = (sectionId) =>
    isHome ? `#${sectionId}` : `/#${sectionId}`

  const closeMenu = () => setMenuOpen(false)

  const accountPath = isAuthed ? '/dashboard' : '/login'

  const showMobileBottomPad =
    !isAppShell &&
    !location.pathname.startsWith('/deposit') &&
    !['/login', '/reset-password', '/get-started', '/onboarding', '/waitlist'].includes(location.pathname)

  return (
    <div className={`page${showMobileBottomPad ? ' page--mobile-bottom-pad' : ''}`}>
      <a className="skip-link" href="#main-content">
        {ui.common.skipToMain}
      </a>
      {!isAppShell && isAuthRoute && (
        <header className="site-header site-header--minimal" role="banner">
          <nav className="nav" aria-label="Main navigation">
            <a className="brand" href={landingHref}>
              <OptimizedImage
                src={akaraLogo}
                webpSrc={akaraLogoWebp}
                alt="Akara logo"
                width="940"
                height="788"
                loading="eager"
                decoding="async"
              />
              <span>Akara</span>
            </a>
          </nav>
        </header>
      )}

      {!isAppShell && !isAuthRoute && (
        <header className={`site-header${isHome ? ' site-header--home' : ''}${scrolled ? ' is-scrolled' : ''}`} role="banner" dir={dir} lang={locale}>
        <nav className="nav" aria-label="Main navigation">
          {/* Absolute landing links are only needed on the app/docs subdomains,
              which render the minimal header (an <a>) above — not this one. React
              Router <Link> resolves its target as an in-app path, so keep it "/". */}
          <Link className="brand" to="/">
            <OptimizedImage
              src={akaraLogo}
              webpSrc={akaraLogoWebp}
              alt="Akara logo"
              width="940"
              height="788"
              loading="eager"
              decoding="async"
            />
            <span>Akara</span>
          </Link>
          <div className="nav-links" onMouseLeave={() => setOpenMenu(null)}>
            <div
              className={`nav-item${openMenu === 'solutions' ? ' is-open' : ''}`}
              onMouseEnter={() => setOpenMenu('solutions')}
            >
              <button
                type="button"
                className="nav-trigger"
                aria-expanded={openMenu === 'solutions'}
                aria-haspopup="true"
                onClick={() =>
                  setOpenMenu(openMenu === 'solutions' ? null : 'solutions')
                }
              >
                {ui.nav.solutions}
                <svg className="nav-chevron" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <div className="nav-dropdown nav-dropdown--mega" role="menu">
                <div className="nav-mega-grid">
                  {SOLUTION_MENU.map((item) => {
                    const m = ui.solutionMenu[item.to.split('/').pop()] || item
                    return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="nav-mega-card"
                      role="menuitem"
                      onClick={() => setOpenMenu(null)}
                    >
                      <span className="nav-mega-ic" aria-hidden="true">
                        {item.icon}
                      </span>
                      <span className="nav-mega-text">
                        <strong>{m.title}</strong>
                        <span>{m.desc}</span>
                      </span>
                    </Link>
                    )
                  })}
                </div>
              </div>
            </div>

            <div
              className={`nav-item${openMenu === 'resources' ? ' is-open' : ''}`}
              onMouseEnter={() => setOpenMenu('resources')}
            >
              <button
                type="button"
                className="nav-trigger"
                aria-expanded={openMenu === 'resources'}
                aria-haspopup="true"
                onClick={() =>
                  setOpenMenu(openMenu === 'resources' ? null : 'resources')
                }
              >
                {ui.nav.resources}
                <svg className="nav-chevron" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <div className="nav-dropdown" role="menu">
                {RESOURCE_MENU.map((item) => {
                  const m = ui.resourceMenu[item.id]
                  return item.external ? (
                    <a
                      key={item.id}
                      href={item.to}
                      className="nav-drop-link"
                      role="menuitem"
                      target="_blank"
                      rel="noopener"
                      onClick={() => setOpenMenu(null)}
                    >
                      <strong>{m.title}</strong>
                      <span>{m.desc}</span>
                    </a>
                  ) : (
                    <Link
                      key={item.id}
                      to={item.to}
                      className="nav-drop-link"
                      role="menuitem"
                      onClick={() => setOpenMenu(null)}
                    >
                      <strong>{m.title}</strong>
                      <span>{m.desc}</span>
                    </Link>
                  )
                })}
              </div>
            </div>

            <Link className="nav-link-plain" to="/pricing">
              {ui.nav.pricing}
            </Link>
            <Link className="nav-link-plain" to="/contact-us">
              {ui.nav.contact}
            </Link>
          </div>
          <div className="nav-actions">
            <LanguageSwitcher />
            <a
              className="ghost-button"
              href={appHref('/login')}
              target="_blank"
              rel="noopener"
            >
              {ui.nav.login}
            </a>
            <Link className="primary-button" to="/pricing">
              {ui.nav.getStarted}
            </Link>
            <div className="nav-menu">
              <button
                className="menu-button"
                type="button"
                aria-label={ui.nav.openMenu}
                aria-expanded={menuOpen}
                aria-controls="nav-menu-list"
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span />
                <span />
                <span />
              </button>
              <div
                id="nav-menu-list"
                className={`nav-menu-list${menuOpen ? ' is-open' : ''}`}
                dir={dir}
                lang={locale}
              >
                <a href={sectionHref('how')} onClick={closeMenu}>
                  {ui.nav.howItWorks}
                </a>
                <a href={sectionHref('solutions')} onClick={closeMenu}>
                  {ui.nav.solutions}
                </a>
                <a href={sectionHref('pricing')} onClick={closeMenu}>
                  {ui.nav.pricing}
                </a>
                <a href={sectionHref('faq')} onClick={closeMenu}>
                  {ui.nav.faq}
                </a>
                <a
                  href={`${DOCS_ORIGIN}/docs`}
                  target="_blank"
                  rel="noopener"
                  onClick={closeMenu}
                >
                  {ui.nav.documentation}
                </a>
                <Link to="/support" onClick={closeMenu}>
                  {ui.nav.support}
                </Link>
                <Link to="/faq" onClick={closeMenu}>
                  {ui.nav.faqPage}
                </Link>
                <a
                  href={appHref('/login')}
                  target="_blank"
                  rel="noopener"
                  onClick={closeMenu}
                >
                  {ui.nav.login}
                </a>
                <div className="nav-menu-lang" role="group" aria-label="Language">
                  {LANGS.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      lang={lang.code}
                      dir={lang.code === 'ar' ? 'rtl' : 'ltr'}
                      className={`nav-menu-lang-option${locale === lang.code ? ' is-active' : ''}`}
                      aria-pressed={locale === lang.code}
                      onClick={() => {
                        setLocale(lang.code)
                        closeMenu()
                      }}
                    >
                      {lang.native}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
      )}

      {!isAppShell && (
        <MobileBottomNav accountPath={accountPath} isAuthed={isAuthed} />
      )}

      <main id="main-content">
        <Suspense
          fallback={
            <div
              className="info-page"
              style={{ textAlign: 'center', paddingTop: '4rem', minHeight: '100vh' }}
            >
              <h1>Loading…</h1>
              <p style={{ color: 'var(--text-subtle)', margin: '1rem 0 2rem' }}>
                Preparing your page.
              </p>
            </div>
          }
        >
          <Routes>
            <Route
              path="/"
              element={isDocsHost ? <Navigate to="/docs" replace /> : <LandingPage />}
            />
            <Route path="/docs" element={<DocsLayout />}>
              <Route index element={<DocsHome />} />
              <Route path="quickstart" element={<DocsQuickstart />} />
              <Route path="api" element={<DocsApiReference />} />
              <Route path="integrations" element={<DocsIntegrations />} />
              <Route path="integrations/:slug" element={<DocsIntegrationDetail />} />
            </Route>
            <Route path="/solutions/property-managers" element={<PropertyManagers />} />
            <Route path="/solutions/healthcare" element={<Healthcare />} />
            <Route path="/solutions/vehicle-rental" element={<VehicleRental />} />
            <Route path="/solutions/equipment-rental" element={<EquipmentRental />} />
            <Route path="/deposit-free" element={<DepositFree />} />
            <Route path="/how-card-holds-work" element={<HowCardHoldsWork />} />
            {/* Old docs pages moved to /docs (Netlify 301s these in prod;
                these Navigates keep dev + SPA fallback consistent). */}
            <Route path="/documentation" element={<Navigate to="/docs" replace />} />
            <Route path="/documentation/webhooks" element={<Navigate to="/docs/api" replace />} />
            <Route path="/support" element={<Support />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/waitlist" element={<Waitlist />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/deposit/:token" element={<DepositView />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardHome />} />
              <Route path="requests" element={<DashboardRequests />} />
              <Route path="claims" element={<DashboardClaims />} />
              <Route path="reports" element={<DashboardReports />} />
              <Route path="integrations" element={<DashboardIntegrations />} />
              <Route path="webhooks" element={<DashboardWebhooks />} />
              <Route path="billing" element={<DashboardBilling />} />
              <Route path="notifications" element={<DashboardNotifications />} />
              <Route path="settings">
                <Route index element={<Navigate to="account" replace />} />
                <Route path="account" element={<DashboardSettingsAccount />} />
                <Route path="payouts" element={<DashboardSettingsPayouts />} />
                <Route path="security" element={<DashboardSettingsSecurity />} />
              </Route>
            </Route>
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminShell />
                </AdminRoute>
              }
            >
              <Route index element={<AdminHome />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:userId" element={<AdminUserDetail />} />
              <Route path="deposits" element={<AdminDeposits />} />
              <Route path="claims" element={<AdminClaims />} />
              <Route path="escalations" element={<AdminEscalations />} />
            </Route>
            <Route
              path="*"
              element={
                <div className="info-page" style={{ textAlign: 'center', paddingTop: '4rem' }}>
                  <h1>Page not found</h1>
                  <p style={{ color: 'var(--text-subtle)', margin: '1rem 0 2rem' }}>
                    The page you&rsquo;re looking for doesn&rsquo;t exist or has been moved.
                  </p>
                  <Link className="primary-button" to="/">
                    Back to home
                  </Link>
                </div>
              }
            />
          </Routes>
        </Suspense>
      </main>

      {!isAppShell && <CookieBanner />}

      {!isAppShell && (
        <footer className="footer" role="contentinfo" dir={dir} lang={locale}>
          <div className="footer-top">
            <div className="footer-brand">
              <OptimizedImage
                src={akaraLogo}
                webpSrc={akaraLogoWebp}
                alt="Akara"
                className="footer-logo"
                width="940"
                height="788"
                loading="lazy"
                decoding="async"
              />
              <strong>Akara</strong>
              <span>{ui.footer.tagline}</span>
            </div>
            <div className="footer-columns">
              <div>
              <h3>{ui.footer.product}</h3>
                <a href={sectionHref('solutions')}>{ui.footer.solutions}</a>
                <Link to="/how-card-holds-work">{ui.footer.howCardHolds}</Link>
                <Link to="/deposit-free">{ui.footer.depositFree}</Link>
                <a href={sectionHref('pricing')}>{ui.footer.pricing}</a>
                <Link to="/waitlist">{ui.footer.joinWaitlist}</Link>
                <Link to="/faq">{ui.footer.faq}</Link>
              </div>
              <div>
              <h3>{ui.footer.resources}</h3>
                <Link to="/blog">{ui.footer.blog}</Link>
                <a href={`${DOCS_ORIGIN}/docs`} target="_blank" rel="noopener">{ui.footer.documentation}</a>
                <Link to="/support">{ui.footer.support}</Link>
                <Link to="/privacy-policy">{ui.footer.privacyPolicy}</Link>
                <Link to="/terms">{ui.footer.terms}</Link>
              </div>
              <div>
              <h3>{ui.footer.connect}</h3>
                <Link to="/contact-us">{ui.footer.contactUs}</Link>
              </div>
            </div>
          </div>
          <div className="footer-social-row">
            <div className="footer-social-links" aria-label="Social media links">
              <a
                href="https://www.instagram.com/akara.ae/"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                title="Instagram"
                className="footer-social-link"
              >
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M8 0C5.83 0 5.56.01 4.7.05 3.85.09 3.27.22 2.76.42a3.87 3.87 0 0 0-1.4.91A3.87 3.87 0 0 0 .45 2.74c-.2.5-.33 1.08-.37 1.94C.01 5.54 0 5.81 0 8c0 2.19.01 2.46.05 3.3.04.86.17 1.44.37 1.95.2.5.47.93.91 1.37.44.44.87.71 1.37.91.5.2 1.08.33 1.94.37.85.04 1.12.05 3.31.05 2.18 0 2.45-.01 3.3-.05.86-.04 1.44-.17 1.95-.37a3.87 3.87 0 0 0 1.37-.91c.44-.44.71-.87.91-1.37.2-.5.33-1.08.37-1.94.04-.85.05-1.12.05-3.31 0-2.18-.01-2.45-.05-3.3-.04-.86-.17-1.44-.37-1.95a3.87 3.87 0 0 0-.91-1.37 3.87 3.87 0 0 0-1.37-.91c-.5-.2-1.08-.33-1.94-.37C10.45.01 10.18 0 8 0m0 1.44h.76c1.78 0 1.99 0 2.8.04.76.04 1.17.16 1.45.27.37.15.64.32.92.6.28.28.45.55.6.92.11.28.23.69.27 1.45.04.82.04 1.03.04 2.8v.96c0 1.78 0 1.99-.04 2.8-.04.76-.16 1.17-.27 1.45-.15.37-.32.64-.6.92-.28.28-.55.45-.92.6-.28.11-.69.23-1.45.27-.82.04-1.03.04-2.8.04h-.96c-1.78 0-1.99 0-2.8-.04-.76-.04-1.17-.16-1.45-.27a2.44 2.44 0 0 1-.92-.6 2.44 2.44 0 0 1-.6-.92c-.11-.28-.23-.69-.27-1.45-.04-.82-.04-1.03-.04-2.8v-.96c0-1.78 0-1.99.04-2.8.04-.76.16-1.17.27-1.45.15-.37.32-.64.6-.92.28-.28.55-.45.92-.6.28-.11.69-.23 1.45-.27.71-.03.99-.04 2.04-.04z" />
                  <path d="M8 3.89A4.11 4.11 0 1 0 8 12.1 4.11 4.11 0 0 0 8 3.89m0 6.78A2.67 2.67 0 1 1 8 5.33a2.67 2.67 0 0 1 0 5.34m4.27-7.9a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92" />
                </svg>
                <span className="sr-only">Instagram</span>
              </a>
              <a
                href="https://x.com/akara_ae"
                target="_blank"
                rel="noreferrer"
                aria-label="X"
                title="X"
                className="footer-social-link"
              >
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M12.6.4h2.3L9.86 6.16 16 15.6h-4.8L7.5 10.3 2.9 15.6H.6l5.4-6.2L0 .4h4.9l3.34 4.9z" />
                </svg>
                <span className="sr-only">X</span>
              </a>
              <a
                href="https://www.linkedin.com/company/akaraportal/"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                title="LinkedIn"
                className="footer-social-link"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6.94 8.5H3.56V20h3.38V8.5ZM5.25 3A1.97 1.97 0 1 0 5.25 6.94A1.97 1.97 0 0 0 5.25 3ZM20.44 13.37C20.44 10.07 18.68 8.27 16.33 8.27C14.43 8.27 13.58 9.31 13.1 10.04V8.5H9.72C9.77 9.52 9.72 20 9.72 20h3.38v-6.42c0-.34.02-.68.12-.92.27-.68.88-1.38 1.9-1.38 1.34 0 1.88 1.02 1.88 2.52V20H20.44v-6.63Z" />
                </svg>
                <span className="sr-only">LinkedIn</span>
              </a>
              <a
                href="https://www.facebook.com/akaraae"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                title="Facebook"
                className="footer-social-link"
              >
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M16 8a8 8 0 1 0-9.25 7.9v-5.6H4.72V8h2.03V6.3c0-2.01 1.2-3.12 3.03-3.12.87 0 1.79.15 1.79.15v1.98h-1.01c-1 0-1.31.62-1.31 1.25V8h2.23l-.36 2.3H9.25v5.6A8 8 0 0 0 16 8" />
                </svg>
                <span className="sr-only">Facebook</span>
              </a>
            </div>
          </div>
          <div className="footer-bottom">
            <span>{ui.footer.rights}</span>
            <div className="footer-bottom-right">
              <div className="footer-bottom-links">
                <Link to="/privacy-policy">{ui.footer.privacy}</Link>
                <span>&middot;</span>
                <Link to="/terms">{ui.footer.terms}</Link>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  )
}

export default App
